import { PageType } from 'sharedEnums/page-type';
import { PersonType } from 'enums/person-type';
import { PersonalActions, PersonalSelectors } from 'stores/personal/personal.module';
import { Store } from '@ngrx/store';
import { LoggingService } from 'sharedServices/logging.service';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { PayloadAction } from 'models/payload-action';
import { isPlatformBrowser } from '@angular/common';
import { selectPageType } from 'sharedStores/quote-controller/quote-controller.selectors';

@Injectable({ providedIn: 'root' })
export class PageTypeService {
    loggingService = inject(LoggingService);
    store = inject(Store);
    platformId = inject(PLATFORM_ID);

    // Get effective page type for personal details pages, use new PageType if available,
    // else fallback to old PersonType
    getPageType(personType: PersonType, componentName: string): PageType | PersonType {
        const pageType = this.store.selectSignal(selectPageType)();
        if (!pageType && isPlatformBrowser(this.platformId)) {
            //If pageType null/undefined, ie. not being used by page yet, log it
            this.loggingService.log({
                message: 'PageType not set for page or component: ' + componentName,
                level: 'Warn',
            });
        }
        return pageType ?? personType;
    }

    handleDispatchAction(pageType: PageType | PersonType, options: DispatchOptions = {}) {
        const { spouseDispatch, householdMemberDispatch, policyHolderDispatch } = options;

        switch (pageType) {
            case PageType.Spouse:
                if (spouseDispatch?.payload) {
                    this.store.dispatch(
                        PersonalActions.updateHouseholdMember(spouseDispatch.payload),
                    );
                }
                break;

            case PersonType.HOUSEHOLD_MEMBER:
            case PageType.HouseholdMember:
                if (householdMemberDispatch?.actionType && householdMemberDispatch?.payload) {
                    this.store.dispatch(
                        PersonalActions.householdMemberActions[householdMemberDispatch.actionType](
                            householdMemberDispatch.payload,
                        ),
                    );
                }
                break;

            case PersonType.POLICY_HOLDER:
            case PageType.PolicyHolder:
            default:
                if (policyHolderDispatch?.actionType && policyHolderDispatch?.payload) {
                    this.store.dispatch(
                        PersonalActions.personalActions[policyHolderDispatch.actionType](
                            policyHolderDispatch.payload,
                        ),
                    );
                }
                break;
        }
    }

    // Return correct selector based on PageType/PersonType passed in
    // Will first try with new PageType, else uses old PersonType
    selectPerson(pageOrPersonType?: PageType | PersonType) {
        if (pageOrPersonType && Object.values(PageType).includes(pageOrPersonType as PageType)) {
            switch (pageOrPersonType as PageType) {
                case PageType.PolicyHolder:
                    return PersonalSelectors.selectPolicyHolder;
                case PageType.HouseholdMember:
                    return PersonalSelectors.selectRegularDriver;
                case PageType.Spouse:
                    return PersonalSelectors.selectSpouse;
            }
        } // use personType
        else {
            switch (pageOrPersonType) {
                case PersonType.HOUSEHOLD_MEMBER:
                    return PersonalSelectors.selectRegularDriver;
                case PersonType.POLICY_HOLDER:
                default:
                    return PersonalSelectors.selectPolicyHolder;
            }
        }
    }

    // Get isPolicyHolder boolean from new PageType, or PersonType as fall back.
    isPolicyHolder(pageType: PageType | PersonType): boolean {
        return pageType === PageType.PolicyHolder || pageType === PersonType.POLICY_HOLDER;
    }

    // Get isHouseholdMember boolean from new PageType, or PersonType as fall back.
    isHouseholdMember(pageType: PageType | PersonType): boolean {
        return pageType === PageType.HouseholdMember || pageType === PersonType.HOUSEHOLD_MEMBER;
    }

    // Get isSpouse boolean from new PageType, or PersonType (householdmember) as fall back.
    isSpouse(pageType: PageType | PersonType): boolean {
        return pageType === PageType.Spouse || pageType === PersonType.HOUSEHOLD_MEMBER;
    }
}

interface DispatchOptions {
    spouseDispatch?: PayloadAction;
    householdMemberDispatch?: PayloadAction;
    policyHolderDispatch?: PayloadAction;
}
